/**
 * Чернетки НЕЗБЕРЕЖЕНИХ форм (рішення власника 2026-08-24).
 *
 * ЗАКОН цього механізму: у БАЗУ автоматично НІЧОГО не пишемо. Незбережені
 * правки живуть ЛИШЕ у браузері (localStorage), прив'язані до tenant+шляху
 * документа. Автозапис у базу створив би «сміттєві» документи, з'їв номер
 * нумерації, показав їх колегам і потребував ручного видалення — тобто
 * зламав би процес «відкрив подивитись і передумав».
 *
 * Життя чернетки — 10 годин (рішення власника). Стирається: після
 * успішного збереження документа, при закритті вкладки без збереження,
 * при відмові відновлювати і за строком.
 */
import { useAuth } from '@/auth/store';
import { isPageLeaving } from './pageLeaving';

/** Скільки живе чернетка (мс). */
export const DRAFT_TTL_MS = 10 * 60 * 60 * 1000;

// v2 (2026-08-24): попередня версія писала чернетку від ПРОГРАМНОГО
// заповнення форми — такі «фантомні» чернетки ігноруємо і прибираємо.
const PREFIX = 'formDraft2.';
const LEGACY_PREFIX = 'formDraft.';

interface StoredDraft<T = unknown> {
  ts: number;
  value: T;
}

function tenant(): string {
  return useAuth.getState().activeTenantId ?? 'no-tenant';
}

export function draftKey(path: string): string {
  return `${PREFIX}${tenant()}|${path}`;
}

export function saveDraft<T>(path: string, value: T): void {
  if (isPageLeaving()) return;   // ч.1197: чернетки скинуто, сторінка перезавантажується
  try {
    localStorage.setItem(draftKey(path),
      JSON.stringify({ ts: Date.now(), value } satisfies StoredDraft<T>));
  } catch {
    /* приватний режим / переповнене сховище — мовчки без чернетки */
  }
}

export function loadDraft<T>(path: string): { value: T; ts: number } | null {
  try {
    const raw = localStorage.getItem(draftKey(path));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft<T>;
    if (!parsed || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > DRAFT_TTL_MS) {
      localStorage.removeItem(draftKey(path));
      return null;
    }
    return { value: parsed.value, ts: parsed.ts };
  } catch {
    return null;
  }
}

export function clearDraft(path: string): void {
  try {
    localStorage.removeItem(draftKey(path));
  } catch { /* ignore */ }
}

export function hasDraft(path: string): boolean {
  return loadDraft(path) !== null;
}

/** Прибирання протермінованих чернеток (виклик на старті застосунку). */
export function purgeExpiredDrafts(): void {
  try {
    const dead: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(LEGACY_PREFIX) && !k.startsWith(PREFIX)) { dead.push(k); continue; }
      if (!k?.startsWith(PREFIX)) continue;
      try {
        const parsed = JSON.parse(localStorage.getItem(k) ?? 'null') as StoredDraft | null;
        if (!parsed || Date.now() - parsed.ts > DRAFT_TTL_MS) dead.push(k);
      } catch {
        dead.push(k);
      }
    }
    dead.forEach((k) => localStorage.removeItem(k));
  } catch { /* ignore */ }
}

/**
 * Попередження браузера при закритті вкладки/вікна з незбереженими
 * правками (пункт 3 плану власника — «випадково закрив»). Реєструється
 * один раз; сторінки лише позначають себе «брудними».
 */
const dirtyPaths = new Set<string>();

export function markDirty(path: string): void {
  if (isPageLeaving()) return;
  dirtyPaths.add(path);
  ensureBeforeUnload();
}

export function markClean(path: string): void {
  dirtyPaths.delete(path);
}

export function isAnyDirty(): boolean {
  return dirtyPaths.size > 0;
}

/** Ключі чернеток ПОТОЧНОГО клієнта у сховищі браузера. */
function tenantDraftKeys(): string[] {
  const prefix = `${PREFIX}${tenant()}|`;
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(prefix)) keys.push(k);
    }
  } catch { /* ignore */ }
  return keys;
}

/**
 * ч.1197: сторінки поточного клієнта з НЕЗБЕРЕЖЕНИМИ правками — чинні
 * чернетки у сховищі + сторінки, позначені «брудними» (чернетка ще не
 * записалась — debounce). Для попередження перед зміною клієнта.
 */
export function unsavedPaths(): string[] {
  const out = new Set<string>(dirtyPaths);
  const prefix = `${PREFIX}${tenant()}|`;
  // Ключі збираємо ДО перевірки: loadDraft прибирає протерміновані.
  for (const k of tenantDraftKeys()) {
    const path = k.slice(prefix.length);
    if (loadDraft(path)) out.add(path);
  }
  return [...out];
}

/**
 * ч.1197 (рішення власника): зміна клієнта скидає роботу в поточному —
 * усі його чернетки стираються, позначки «брудна» знімаються (інакше
 * браузер показав би ще й власне «покинути сторінку?»).
 */
export function discardAllDrafts(): void {
  dirtyPaths.clear();
  try {
    tenantDraftKeys().forEach((k) => localStorage.removeItem(k));
  } catch { /* ignore */ }
}

let beforeUnloadBound = false;

function ensureBeforeUnload(): void {
  if (beforeUnloadBound) return;
  beforeUnloadBound = true;
  window.addEventListener('beforeunload', (e) => {
    if (!isAnyDirty()) return;
    e.preventDefault();
    // Текст задає сам браузер; повернене значення лише вмикає діалог.
    e.returnValue = '';
  });
}

/**
 * ч.1059: «правка користувача» без події вводу з клавіатури. Чернетка
 * пишеться лише після РЕАЛЬНОЇ дії людини (фікс фантомних чернеток ч.585):
 * слухаємо input/change/paste у .app__main. Але вибір мишею у підборах
 * (оргформа, дата з календаря, КАТОТТГ, УКТЗЕД, ДПІ) і кнопки «додати/
 * прибрати рядок» таких подій не дають — без сигналу правка, зроблена ЛИШЕ
 * ними, не потрапила б у чернетку.
 *
 * `el` — елемент підбору: подія спливає по DOM, тож підбір у шапці сайту
 * (поза .app__main) чернетку сторінки не зачіпає. Без `el` — сигнал
 * сторінці (.app__main) напряму.
 */
export const USER_EDIT_EVENT = 'formdraft:user-edit';

export function signalUserEdit(el?: EventTarget | null): void {
  const target = el ?? (typeof document !== 'undefined'
    ? document.querySelector('.app__main') : null);
  target?.dispatchEvent(new Event(USER_EDIT_EVENT, { bubbles: true }));
}
